import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/config';
import { setAuth } from '../../utils/authStorage';
import { AuthPage, EyeIcon } from './AuthShell';
import './LoginPage.css';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  // If no token, show "forgot password" form (request reset email)
  // If token present, show "new password" form
  const [mode] = useState(token ? 'reset' : 'forgot');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setSuccess(data.message || 'Check your email for a reset link.');
    } catch {
      setError('Cannot connect to server');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (data.success && data.token) {
        setAuth({ token: data.token, user: data.user, remember: true });
        setSuccess('Password reset! Redirecting...');
        setTimeout(() => navigate('/'), 1500);
      } else {
        setError(data.error || 'Reset failed');
      }
    } catch {
      setError('Cannot connect to server');
    }
    setLoading(false);
  };

  return (
    <AuthPage>
      <p className="login-subtitle">{mode === 'forgot' ? 'Reset your password' : 'Choose a new password'}</p>

      {mode === 'forgot' ? (
        <form className="login-form" onSubmit={handleForgot}>
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
          {error && <div className="login-error">{error}</div>}
          {success && <div className="login-success">{success}</div>}
          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
      ) : (
        <form className="login-form" onSubmit={handleReset}>
          <div className="login-password-wrapper">
            <input type={showPassword ? 'text' : 'password'} placeholder="New password" value={password} onChange={e => setPassword(e.target.value)} minLength={6} required autoFocus />
            <button type="button" className="login-eye-btn" onClick={() => setShowPassword(v => !v)} tabIndex={-1}><EyeIcon open={showPassword} /></button>
          </div>
          <div className="login-password-wrapper">
            <input type={showPassword ? 'text' : 'password'} placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} minLength={6} required />
          </div>
          {error && <div className="login-error">{error}</div>}
          {success && <div className="login-success">{success}</div>}
          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      )}

      <div className="login-switch">
        <button
          type="button"
          className="login-link"
          onClick={() => {
            // If this tab was opened from the marketing-site modal
            // (Forgot password? link there uses target="_blank"),
            // closing drops the user straight back to where they
            // came from. Browsers only allow window.close() on tabs
            // opened programmatically or via target="_blank", which
            // this path always is — but we fall back to a hard nav
            // anyway for anyone who landed here via a reset email.
            if (window.opener && !window.opener.closed) {
              window.close();
              return;
            }
            navigate('/login');
          }}
        >
          Back to Sign In
        </button>
      </div>
    </AuthPage>
  );
}
